import React from 'react'

import { Gem, Minus, Plus } from 'lucide-react'

import { useTranslation } from '@/core/i18n/useTranslation'
import { Button } from '@/shared/components/atoms/Button'
import { cn } from '@/shared/utils/helpers'

import { BillingCycle, PlanConfig } from '../types'

interface PlanCardProps {
	plan: PlanConfig
	isSelected: boolean
	billingCycle: BillingCycle
	zonesCount: number
	minZones?: number
	onSelect: () => void
	onZonesChange: (count: number) => void
	onContactUs?: () => void
}

export const PlanCard: React.FC<PlanCardProps> = ({
	plan,
	isSelected,
	billingCycle,
	zonesCount,
	minZones = 1,
	onSelect,
	onZonesChange,
	onContactUs,
}) => {
	const { t } = useTranslation('common')

	const price = billingCycle === 'annual' ? plan.priceAnnual : plan.priceMonthly

	const handleDecrement = (e: React.MouseEvent) => {
		e.stopPropagation()
		if (zonesCount > minZones) {
			onZonesChange(zonesCount - 1)
		}
	}

	const handleIncrement = (e: React.MouseEvent) => {
		e.stopPropagation()
		if (zonesCount < 100) {
			onZonesChange(zonesCount + 1)
		}
	}

	// Signature plan - Contact Us
	if (plan.isContactUs) {
		return (
			<div
				className={cn(
					'p-6 rounded-xl border transition-all cursor-pointer',
					isSelected
						? 'border-main-accent bg-card'
						: 'border-border bg-card/50 hover:border-border/80'
				)}
				onClick={onSelect}
			>
				<div className="flex flex-col lg:flex-row items-start justify-between">
					<div>
						<div className="flex items-center gap-2 mb-2">
							<Gem className="h-4 w-4 text-primary" />
							<h3 className="text-lg font-semibold">{plan.name}</h3>
						</div>
						<p className="text-sm text-muted-foreground max-w-md">
							{plan.description}
						</p>
					</div>
					<Button
						variant="outline"
						size="sm"
						className="rounded-full"
						onClick={(e) => {
							e.stopPropagation()
							onContactUs?.()
						}}
					>
						{t('subscribe.contact_us')}
					</Button>
				</div>
			</div>
		)
	}

	return (
		<div
			className={cn(
				'p-6 rounded-xl border transition-all cursor-pointer',
				isSelected
					? 'border-main-accent bg-card'
					: 'border-border bg-card/50 hover:border-border/80'
			)}
			onClick={onSelect}
		>
			<div className="flex flex-col lg:flex-row items-start justify-between">
				<div className="flex-1">
					<h3 className="text-lg font-semibold mb-2">{plan.name}</h3>
					<p className="text-sm text-muted-foreground max-w-md hidden lg:block">
						{plan.description}
					</p>
				</div>

				<div className="flex lg:items-center justify-between lg:justify-start lg:gap-8 w-full lg:w-auto">
					{/* Zone Selector */}
					{plan.hasZoneSelector && isSelected && (
						<div className="flex flex-col lg:items-end">
							<span className="text-sm text-muted-foreground mb-2">
								{t('subscribe.number_of_zones')}
							</span>
							<div className="flex items-center gap-3">
								<button
									aria-label="Decrease zones"
									onClick={handleDecrement}
									disabled={zonesCount <= minZones}
									className="h-9 w-9 rounded-full bg-muted flex items-center justify-center hover:bg-muted/80 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
								>
									<Minus className="h-4 w-4" />
								</button>
								<span className="text-xl font-semibold w-8 text-center">
									{zonesCount}
								</span>
								<button
									aria-label="Increase zones"
									onClick={handleIncrement}
									disabled={zonesCount >= 100}
									className="h-9 w-9 rounded-full bg-muted flex items-center justify-center hover:bg-muted/80 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
								>
									<Plus className="h-4 w-4" />
								</button>
							</div>
						</div>
					)}

					{/* Price */}
					<div className="text-right flex items-end">
						<span className="lg:text-2xl font-bold">
							{price} {plan.currency}
						</span>
						<span className="text-sm text-muted-foreground">
							/{t('subscribe.zone')}
						</span>
					</div>
				</div>
			</div>
		</div>
	)
}

export default PlanCard
